/**
 * The consultant — API client
 */

import { api } from '../../services/api';

export interface PlaybookView {
  key: string;
  label: string;
  /** The standing question itself, in the words it will be asked in. */
  question: string;
  /** The condition that makes it worth running now. */
  trigger: string;
  metrics: string[];
  blocked: boolean;
}

export interface AgentView {
  key: string;
  label: string;
  mandate: string;
  ownedMetrics: string[];
  playbooks: PlaybookView[];
}

export interface AcceptedClaim {
  statement: string;
  citations: string[];
  value?: number | null;
  metricKey?: string | null;
}

export interface RejectedClaim {
  statement: string;
  reason: string;
  detail?: string;
}

export interface MeasuredEvidence {
  citationId: string;
  key: string;
  label: string;
  value: number | null;
  unit: string;
  currency: string | null;
  certificationState: string;
  owner: string | null;
  comparison: { value: number | null; changePercent: number | null } | null;
  rowsConsidered: number;
  warnings: string[];
}

export interface KnowledgeEvidence {
  citationId: string;
  statement: string;
  title: string;
  author: string | null;
  kind: string;
  certification: string;
  locator: string | null;
}

export interface SignalEvidence {
  citationId: string;
  headline: string;
  detail: string;
  scope: string;
  source: string;
  observedAt: string;
  ageDays: number;
  reliability: string;
  direction: string;
  figures: Array<{ label: string; value: number; unit: string }>;
  contested: boolean;
}

export interface ProcessEvidence {
  citationId: string;
  prompt: string;
  answer: string;
  statedBy: string;
  statedAt: string;
  isConstraint: boolean;
}

export interface AskResult {
  agentKey: string;
  question: string;
  referenceDate: string;
  refused: boolean;
  refusalReason: string | null;
  message: string | null;
  finding: {
    headline: string;
    claims: AcceptedClaim[];
    rejected: RejectedClaim[];
    recommendedAction: string | null;
    confidence: string;
  } | null;
  evidence: {
    measured: MeasuredEvidence[];
    knowledge: KnowledgeEvidence[];
    signals: SignalEvidence[];
    process: ProcessEvidence[];
  };
  blockedPlaybooks: Array<{
    playbookKey: string;
    message: string;
    waitingOn: string;
  }>;
  unavailable: Array<{ key: string; reason: string; remedy: string }>;
  activation: { state: string; reason: string };
  toolCalls: Array<{ tool: string; citationId: string; summary: string }>;
  model: { provider: string; model: string; provisional: boolean } | null;
  rounds: number;
  durationMs: number;
  tokens: { input: number; output: number };
}

interface Envelope<T> {
  success: boolean;
  data: T;
}

class ConsultantService {
  async listAgents(): Promise<AgentView[]> {
    const response = await api.get<Envelope<AgentView[]>>('/core/agents');
    return response.data ?? [];
  }

  /**
   * Ask. Passing a playbook is what lets the runtime enforce a block rather
   * than merely mention it, so the UI passes one whenever the question came
   * from a playbook button.
   */
  async ask(
    agentKey: string,
    question: string,
    playbookKey?: string
  ): Promise<AskResult> {
    const response = await api.post<Envelope<AskResult>>(
      `/core/agents/${agentKey}/ask`,
      { question, playbookKey }
    );
    return response.data;
  }
}

export const consultantService = new ConsultantService();
